import { IntegerOverflowError, InvalidRangeError } from "../errors/index.js";
import { MAX, MIN } from "./constants.js";

/**
 * Divide Int128 values (truncate toward zero)
 *
 * @see https://voltaire.tevm.sh/primitives/int128 for Int128 documentation
 * @since 0.0.0
 * @param {import('./Int128Type.js').BrandedInt128} a - Dividend
 * @param {import('./Int128Type.js').BrandedInt128} b - Divisor
 * @returns {import('./Int128Type.js').BrandedInt128} Quotient (truncated toward zero)
 * @throws {InvalidRangeError} If divisor is zero
 * @throws {IntegerOverflowError} If MIN / -1 (overflow)
 * @example
 * ```javascript
 * import * as Int128 from './primitives/Int128/index.js';
 * const a = Int128.from(-10n);
 * const b = Int128.from(3n);
 * const quotient = Int128.dividedBy(a, b); // -3n (not -4n)
 * ```
 */
export function dividedBy(a, b) {
	if (b === 0n) {
		throw new InvalidRangeError("Division by zero", {
			value: b,
			expected: "non-zero divisor",
			docsPath: "/primitives/int128#divided-by",
		});
	}

	// Special case: MIN / -1 overflows
	if (a === MIN && b === -1n) {
		throw new IntegerOverflowError("Int128 overflow: MIN / -1", {
			value: -MIN,
			max: MAX,
			type: "int128",
			context: { operation: "dividedBy", operands: [a, b] },
		});
	}

	return /** @type {import('./Int128Type.js').BrandedInt128} */ (a / b);
}
